import { useEffect, useState } from "react";
import React from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, LogOut, Star, Menu, X, Check, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { ImageField, MultiImageField, absoluteMediaUrl } from "@/components/ImageField";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { ServicesPanel } from "@/components/admin/ServicesPanel";
import { QuotesPanel } from "@/components/admin/QuotesPanel";
import { EstimatesPanel } from "@/components/admin/EstimatesPanel";
import { EstimatorConfigPanel } from "@/components/admin/EstimatorConfigPanel";
import { MessagesPanel } from "@/components/admin/MessagesPanel";
import { ShopQuotesPanel } from "@/components/admin/ShopQuotesPanel";
import { OrdersPanel } from "@/components/admin/OrdersPanel";
import { CrmPanel } from "@/components/admin/CrmPanel";
import { NewsletterContacts } from "@/components/admin/NewsletterContacts";
import { BlogPanel } from "@/components/admin/BlogPanel";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";
import { PagesPanel } from "@/components/admin/PagesPanel";
import { GiftCardsPanel } from "@/components/admin/GiftCardsPanel";
import { LibraryPanel } from "@/components/admin/LibraryPanel";
import { ShoppingListsPanel } from "@/components/admin/ShoppingListsPanel";
import { AutomationsPanel } from "@/components/admin/AutomationsPanel";
import { ShopCategoriesPanel } from "@/components/admin/ShopCategoriesPanel";
import { GalleryManager } from "@/components/admin/GalleryManager";
import { BannersPanel } from "@/components/admin/BannersPanel";
import { SectionImagePanel } from "@/components/admin/SectionImagePanel";
import { RequestsPanel } from "@/components/admin/RequestsPanel";

const BOOKING_STATUS_LABELS = {
  pending: "In attesa", confirmed: "Confermata", cancelled: "Annullata",
};
const BOOKING_STATUS_COLORS = {
  confirmed: "text-emerald-700", cancelled: "text-red-700", pending: "text-gold",
};
const YesNo = ({ v }) => <span className={v ? "text-emerald-700" : "text-taupe/60"}>{v ? "✓" : "—"}</span>;

const empty = { title: "", category: "Residenziale", location: "", year: "", description: "", cover_image: "", images: [], featured: false };
const AVAIL_LABELS = { disponibile: "Disponibile", limitata: "Disponibilità limitata", da_ordinare: "Da ordinare", non_disponibile: "Non disponibile", temporaneamente_non_disponibile: "Temp. non disponibile", esaurito: "Esaurito", su_richiesta: "Su richiesta" };
const AVAIL_COLORS = { disponibile: "text-emerald-700", limitata: "text-amber-700", da_ordinare: "text-amber-700", non_disponibile: "text-red-700", temporaneamente_non_disponibile: "text-red-700", esaurito: "text-red-700", su_richiesta: "text-gold" };

const emptyProduct = { name: "", brand: "", category: "", price: 0, currency: "EUR", image: "", gallery: [], short_description: "", description: "", in_stock: true, featured: false, published: true, availability: "disponibile", lead_time: "", sku: "", compare_price: 0, is_new: false, price_on_request: false, collection: "", materials: "", colors: "", dimensions: "", variants: [], lead_days: "", lead_date: "", purchasable: true, info_request: true, studio_discount_excluded: false, source_url: "" };
const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n);

const SECTIONS = [
  ["portfolio", "Portfolio"], ["services", "Servizi"], ["crm", "Clienti"], ["bookings", "Prenotazioni"],
  ["requests", "Richieste"], ["contacts", "Messaggi"], ["products", "Shop"], ["inquiries", "Preventivi"],
  ["estimates", "Stime"], ["estimator", "Configurazione stima"],
  ["orders", "Ordini"], ["blog", "Blog"], ["banner", "Banner"], ["analytics", "Analytics"],
  ["newsletter", "Newsletter"], ["automazioni", "Automazioni"], ["library", "Library"], ["shoptheproject", "Shop the Project"], ["samples", "Materiali PDF"], ["leads", "Lead PDF"], ["pagine", "Pagine"], ["giftcards", "Gift Card"], ["settings", "Impostazioni"],
  ["backup", "Backup"],
];

function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = () => setM(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return m;
}

function BookingMeta({ b }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-taupe" data-testid={`booking-meta-${b.id}`}>
      <span>Email conferma: <YesNo v={b.email_final_sent} /></span>
      <span>Calendar: <YesNo v={!!b.gcal_event_id} />{b.gcal_event_id && <span className="break-all"> · …{b.gcal_event_id.slice(-8)}</span>}</span>
      {b.last_error && <span className="col-span-2 text-red-700">{b.last_error}</span>}
    </div>
  );
}

function BookingActions({ b, onAction, onConfirm, onEdit, onArchive, onTrash, compact }) {
  const cls = compact
    ? "text-xs hover:underline min-h-[44px] px-1"
    : "min-h-[44px] text-xs uppercase tracking-[0.12em] border px-3 transition-colors";
  return (
    <div className={compact ? "space-x-2" : "mt-4 flex flex-wrap gap-2"} data-testid={`booking-actions-${b.id}`}>
      {b.status === "pending" && (
        <button onClick={() => onConfirm(b)} className={compact ? `${cls} text-emerald-700` : `${cls} border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-bone`} data-testid={`booking-confirm-${b.id}`}>Conferma</button>
      )}
      <button onClick={() => onEdit(b)} className={compact ? `${cls} text-ink` : `${cls} border-ink text-ink hover:bg-ink hover:text-bone`} data-testid={`booking-edit-${b.id}`}>Modifica</button>
      {b.status !== "cancelled" && (
        <button onClick={() => onAction(b, "cancel")} className={compact ? `${cls} text-red-700` : `${cls} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`booking-cancel-${b.id}`}>Annulla</button>
      )}
      {b.status === "confirmed" && (
        <button onClick={() => onAction(b, "resend-confirmation")} className={compact ? `${cls} text-ink` : `${cls} border-ink text-ink hover:bg-ink hover:text-bone`} data-testid={`booking-resend-conf-${b.id}`}>Reinvia conferma</button>
      )}
      {["pending", "error"].includes(b.gcal_status) && b.status !== "cancelled" && (
        <button onClick={() => onAction(b, "retry-gcal")} className={compact ? `${cls} text-ink` : `${cls} border-ink text-ink hover:bg-ink hover:text-bone`} data-testid={`booking-retry-gcal-${b.id}`}>Riprova sincronizzazione Calendar</button>
      )}
      <button onClick={() => onArchive(b)} className={compact ? `${cls} text-taupe` : `${cls} border-hair text-taupe hover:border-ink hover:text-ink`} data-testid={`booking-archive-${b.id}`}>Archivia</button>
      <button onClick={() => onTrash(b)} className={compact ? `${cls} text-red-700` : `${cls} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`booking-trash-${b.id}`}>Elimina</button>
    </div>
  );
}

function ServiceSampleCard({ svc, sample, onSaved }) {
  const slug = svc.slug;
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ title: sample?.title || "", description: sample?.description || "", lead_capture: sample?.lead_capture ?? true, visible: sample?.visible ?? true });
  const ref = React.useRef(null);

  useEffect(() => {
    setMeta({ title: sample?.title || "", description: sample?.description || "", lead_capture: sample?.lead_capture ?? true, visible: sample?.visible ?? true });
  }, [sample]);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/service-samples", { slug, pdf_url: data.url, label: meta.title || "Estratto del progetto", title: meta.title || "Estratto del progetto", description: meta.description, lead_capture: meta.lead_capture });
      toast.success("PDF caricato");
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore upload");
    } finally { setBusy(false); }
  };

  const saveMeta = async () => {
    try {
      await api.put(`/admin/service-samples/${slug}/meta`, meta);
      toast.success("Impostazioni PDF salvate");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    }
  };

  const remove = async () => {
    if (!window.confirm("Rimuovere il PDF di esempio?")) return;
    await api.delete(`/admin/service-samples/${slug}`);
    toast.success("PDF rimosso"); onSaved();
  };

  return (
    <div className="bg-white border border-hair p-5 md:p-6" data-testid={`sample-card-${slug}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="eyebrow">{svc.title}</div>
        <span className="text-[10px] tracking-[0.14em] uppercase text-taupe" data-testid={`sample-requests-${slug}`}>
          {sample?.requests_count ?? 0} richieste
        </span>
      </div>
      {sample?.pdf_url ? (
        <div className="mt-4 space-y-4">
          <a href={absoluteMediaUrl(sample.pdf_url)} target="_blank" rel="noreferrer" className="text-sm text-gold hover:underline break-all block">PDF attuale</a>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Titolo mostrato nel modulo</Label>
              <Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="rounded-none mt-1" data-testid={`sample-title-${slug}`} />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Descrizione</Label>
              <Textarea rows={2} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className="rounded-none mt-1" data-testid={`sample-desc-${slug}`} />
            </div>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <Switch checked={meta.lead_capture} onCheckedChange={(v) => setMeta({ ...meta, lead_capture: v })} data-testid={`sample-lead-toggle-${slug}`} />
              <span>Raccolta lead attiva</span>
            </label>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <Switch checked={meta.visible} onCheckedChange={(v) => setMeta({ ...meta, visible: v })} data-testid={`sample-visible-toggle-${slug}`} />
              <span>Mostra PDF esempio sul sito {meta.visible ? "" : "(nascosto: il PDF e i dati restano salvati)"}</span>
            </label>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={saveMeta} className="min-h-[44px] inline-flex items-center text-xs tracking-[0.14em] uppercase bg-ink text-bone px-4 py-2 hover:bg-gold transition-colors" data-testid={`sample-save-${slug}`}>Salva</button>
            <button onClick={() => ref.current?.click()} disabled={busy} className="min-h-[44px] inline-flex items-center text-xs tracking-[0.14em] uppercase border border-ink px-3 py-2 hover:bg-ink hover:text-bone transition-colors disabled:opacity-60" data-testid={`sample-replace-${slug}`}>Sostituisci</button>
            <button onClick={remove} className="min-h-[44px] inline-flex items-center text-xs tracking-[0.14em] uppercase border border-red-700 text-red-700 px-3 py-2 hover:bg-red-700 hover:text-bone transition-colors" data-testid={`sample-delete-${slug}`}>Rimuovi</button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <p className="text-sm text-taupe">Nessun PDF caricato. La CTA "Ricevi un estratto" resta nascosta sulla pagina del servizio.</p>
          <button onClick={() => ref.current?.click()} disabled={busy} className="mt-3 min-h-[44px] w-full sm:w-auto inline-flex items-center justify-center text-xs tracking-[0.14em] uppercase border border-ink px-4 py-2 hover:bg-ink hover:text-bone transition-colors disabled:opacity-60" data-testid={`sample-upload-${slug}`}>{busy ? "Caricamento…" : "Carica PDF"}</button>
        </div>
      )}
      <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`sample-file-${slug}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, checking, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [samples, setSamples] = useState({});
  const [pdfLeads, setPdfLeads] = useState([]);
  const [servicesForSamples, setServicesForSamples] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return t || "portfolio";
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bForm, setBForm] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [shopCategories, setShopCategories] = useState([]);

  const refresh = async () => {
    const [p, b, c, s, pr, iq, or, sp, st, sv, pl, ct] = await Promise.all([
      api.get("/portfolio"),
      api.get("/admin/bookings"),
      api.get("/admin/contacts"),
      api.get("/admin/stats"),
      api.get("/admin/products"),
      api.get("/admin/inquiries"),
      api.get("/admin/orders"),
      api.get("/admin/service-samples"),
      api.get("/settings"),
      api.get("/admin/services"),
      api.get("/admin/pdf-leads"),
      api.get("/admin/shop/categories"),
    ]);
    setItems(p.data); setBookings(b.data); setContacts(c.data); setStats(s.data);
    setProducts(pr.data); setInquiries(iq.data);
    setOrders(or.data);
        setSamples(sp.data); setSettings(st.data);
    setServicesForSamples(sv.data); setPdfLeads(pl.data); setShopCategories(ct.data);
  };

  const saveSettings = async (updates) => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", updates);
      setSettings(data);
      toast.success("Impostazioni salvate");
    } catch { toast.error("Errore salvataggio"); }
    finally { setSavingSettings(false); }
  };

  const downloadFile = async (url, fallbackName) => {
    try {
      const res = await api.get(url, { responseType: "blob", timeout: 120000 });
      const dispo = res.headers?.["content-disposition"] || "";
      const m = dispo.match(/filename="?([^";]+)"?/);
      const blob = new Blob([res.data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m?.[1] || fallbackName;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Download avviato");
    } catch { toast.error("Errore download"); }
  };

  const doExport = async () => {
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `gm-backup-${new Date().toISOString().slice(0,10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch { toast.error("Errore export"); }
  };

  const downloadCsv = (rows, columns, filename) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [columns.map((c) => esc(c.label)).join(";"),
      ...rows.map((r) => columns.map((c) => esc(typeof c.value === "function" ? c.value(r) : r[c.value])).join(";"))].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV scaricato");
  };

  const exportBookingsCsv = () => downloadCsv(bookings, [
    { label: "Data", value: "date" }, { label: "Ora", value: "time_slot" },
    { label: "Nome", value: "name" }, { label: "Email", value: "email" },
    { label: "Telefono", value: "phone" }, { label: "Tipo", value: "consultation_type" },
    { label: "Stato", value: "status" }, { label: "Note", value: "notes" },
  ], `gm-prenotazioni-${new Date().toISOString().slice(0,10)}.csv`);

  const exportContactsCsv = () => downloadCsv(contacts, [
    { label: "Data", value: (r) => new Date(r.created_at).toLocaleString("it-IT") },
    { label: "Nome", value: "name" }, { label: "Email", value: "email" },
    { label: "Telefono", value: "phone" }, { label: "Oggetto", value: "subject" },
    { label: "Messaggio", value: "message" },
  ], `gm-messaggi-${new Date().toISOString().slice(0,10)}.csv`);

  useEffect(() => { if (admin) refresh().catch(() => {}); }, [admin]);

  if (checking) return <div className="min-h-[60vh] flex items-center justify-center text-taupe">Verifica sessione…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const openNew = () => { setEditingId(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (p) => { setEditingId(p.id); setForm({ ...p, images: p.images || [] }); setDialogOpen(true); };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/portfolio/${editingId}`, form);
        toast.success("Progetto aggiornato");
      } else {
        await api.post("/admin/portfolio", form);
        toast.success("Progetto creato");
      }
      setDialogOpen(false); refresh();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore salvataggio");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare questo progetto?")) return;
    await api.delete(`/admin/portfolio/${id}`);
    toast.success("Progetto eliminato"); refresh();
  };

  const updateBooking = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Prenotazione aggiornata");
      refresh();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore aggiornamento");
    }
  };

  const askConfirmBooking = (b) => updateBooking(b.id, "confirmed");

  const openEditBooking = (b) => {
    setBForm({ date: b.date, time_slot: b.time_slot, name: b.name || "", email: b.email || "", phone: b.phone || "", notes: b.notes || "" });
    setEditingBooking(b);
  };
  const saveBookingEdit = async () => {
    try {
      await api.patch(`/admin/bookings/${editingBooking.id}`, bForm);
      toast.success("Prenotazione aggiornata. Se Google Calendar è collegato, l'evento è stato sincronizzato.");
      setEditingBooking(null);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Aggiornamento non riuscito"); }
  };
  const archiveBooking = async (b) => {
    if (!window.confirm(`Archiviare la prenotazione di ${b.name}? Resta conservata nello storico (sezione Richieste) ma esce dall'elenco attivo.`)) return;
    try { await api.post("/admin/requests/archive", { items: [{ type: "booking", id: b.id }] }); toast.success("Prenotazione archiviata"); refresh(); }
    catch { toast.error("Archiviazione non riuscita"); }
  };
  const trashBooking = async (b) => {
    if (!window.confirm(`Eliminare la prenotazione di ${b.name}?\n\nVerrà spostata nel Cestino (recuperabile per 30 giorni dalla sezione Richieste) e l'evento Google Calendar collegato sarà cancellato.`)) return;
    try { await api.post("/admin/requests/trash", { items: [{ type: "booking", id: b.id }] }); toast.success("Prenotazione spostata nel Cestino"); refresh(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  const bookingAction = async (b, action) => {
    if (action === "cancel") { updateBooking(b.id, "cancelled"); return; }
    try {
      await api.post(`/admin/bookings/${b.id}/${action}`);
      toast.success({ "resend-payment-link": "Link di pagamento reinviato", "resend-confirmation": "Conferma reinviata", "retry-gcal": "Sincronizzazione Calendar riuscita" }[action] || "Fatto");
      refresh();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Operazione non riuscita");
    }
  };

  const openNewProduct = () => { setEditingProductId(null); setProductForm(emptyProduct); setProductDialogOpen(true); };
  const analyzeImport = async () => {
    if (!importUrl.trim()) return;
    setImportBusy(true);
    try {
      const { data: d } = await api.post("/admin/products/import-url", { url: importUrl.trim() });
      setEditingProductId(null);
      setProductForm({
        ...emptyProduct, published: false,
        name: d.name || "", brand: d.brand || "", collection: d.collection || "",
        description: d.description || "", short_description: (d.description || "").slice(0, 160),
        sku: d.sku || "", image: d.image || "",
        gallery: (d.images || []).filter((u) => u !== d.image).slice(0, 6).map((u) => ({ url: u, alt: d.name || "" })),
        dimensions: d.dimensions || "", materials: d.materials || "", colors: d.colors || "",
        source_url: d.source_url || "",
      });
      setImportOpen(false); setImportUrl("");
      setProductDialogOpen(true);
      toast.success("Bozza importata: verifica i dati, imposta prezzo e disponibilità manualmente, poi salva.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Importazione non riuscita");
    } finally { setImportBusy(false); }
  };
  const openEditProduct = (p) => { setEditingProductId(p.id); setProductForm({ ...p, gallery: p.gallery || [] }); setProductDialogOpen(true); };

  const onSaveProduct = async (e) => {
    e.preventDefault();
    if (productForm.availability === "da_ordinare" && !productForm.lead_time) {
      toast.error("Per 'Da ordinare' la tempistica è obbligatoria");
      return;
    }
    try {
      const payload = { ...productForm, price: parseFloat(productForm.price) };
      if (editingProductId) {
        await api.put(`/admin/products/${editingProductId}`, payload);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Prodotto creato");
      }
      setProductDialogOpen(false); refresh();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore salvataggio");
    }
  };

  const onDeleteProduct = async (id) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Prodotto eliminato"); refresh();
  };

  const updateInquiry = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status });
    toast.success("Richiesta aggiornata"); refresh();
  };

  const setP = (k) => (e) => setProductForm({ ...productForm, [k]: e.target?.value ?? e });


  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  return (
    <div className="min-h-screen bg-bone overflow-x-clip" data-testid="page-admin-dashboard">
      <div className="border-b border-hair bg-white">
        <div className="container-x flex items-center justify-between h-20">
          <Link to="/" className="flex flex-col leading-none">
            <span className="font-serif text-2xl tracking-tight">Giovanna Matino</span>
            <span className="eyebrow mt-1 !text-[9px] sm:!text-[11px] !tracking-[0.18em] sm:!tracking-[0.28em]">Admin · Interior Design Studio</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="text-sm text-taupe hidden sm:block">{admin.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 min-h-[44px] text-sm text-ink hover:text-gold" data-testid={ADMIN.logout}>
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </div>

      <div className="container-x py-6 md:py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4 mb-4 md:mb-5">
          {[["Nuovi lead", stats.leads_new ?? 0],["Stime", stats.estimates_total ?? 0],["Consulenze", stats.bookings_count ?? 0],["Book scaricati", stats.book_downloads ?? 0],["Iscritti newsletter", stats.newsletter_active ?? 0],["Clienti", stats.clients_count ?? 0]].map(([l,v])=>(
            <div key={l} className="bg-white border border-hair p-4 min-h-[92px] flex flex-col justify-between" data-testid={`stat-card-${l.toLowerCase().replace(/\s+/g,"-")}`}>
              <div className="eyebrow text-[10px] leading-snug">{l}</div>
              <div className="font-serif text-3xl mt-1">{v}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-8 md:mb-10">
          {[["Ordini Shop", stats.paid_orders ?? 0],["Fatturato Shop", `€ ${(stats.shop_revenue ?? 0).toLocaleString("it-IT")}`],["Shopping List", stats.shopping_lists_count ?? 0],["Valore StP", `€ ${(stats.stp_revenue ?? 0).toLocaleString("it-IT")}`],["Prodotti", stats.products_count ?? 0],["Messaggi", stats.contacts_count ?? 0]].map(([l,v])=>(
            <div key={l} className="bg-white border border-hair p-4 min-h-[92px] flex flex-col justify-between" data-testid={`stat-card-${l.toLowerCase().replace(/\s+/g,"-")}`}>
              <div className="eyebrow text-[10px] leading-snug">{l}</div>
              <div className="font-serif text-2xl mt-1">{v}</div>
            </div>
          ))}
        </div>
        {stats.leads_by_source && (
          <div className="mb-8 md:mb-10 flex flex-wrap gap-2" data-testid="stats-leads-source">
            <span className="text-[10px] tracking-[0.14em] uppercase text-taupe self-center mr-1">Provenienza lead:</span>
            {Object.entries(stats.leads_by_source).map(([k, v]) => (
              <span key={k} className="text-[10px] tracking-[0.12em] uppercase border border-hair bg-white px-3 py-1.5 text-taupe">{k.replace(/_/g, " ")} · <strong className="text-ink">{v}</strong></span>
            ))}
          </div>
        )}

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setMenuOpen(false); }}>
          {/* Mobile: menu a pannello verticale */}
          <div className="md:hidden mb-6">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="w-full min-h-[48px] bg-white border border-hair px-4 flex items-center justify-between text-sm tracking-[0.14em] uppercase"
              data-testid="admin-mobile-menu-toggle"
              aria-expanded={menuOpen}
            >
              <span>Menu dashboard · <span className="text-gold normal-case font-serif text-base tracking-normal">{SECTIONS.find(([v]) => v === tab)?.[1]}</span></span>
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            {menuOpen && (
              <div className="bg-white border border-hair border-t-0 divide-y divide-hair" data-testid="admin-mobile-menu">
                {SECTIONS.map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => { setTab(v); setMenuOpen(false); }}
                    data-testid={`admin-mobile-nav-${v}`}
                    className={`w-full text-left px-4 min-h-[48px] flex items-center justify-between text-sm transition-colors ${tab === v ? "bg-ink text-bone" : "text-ink hover:bg-bone"}`}
                  >
                    <span className="inline-flex items-center gap-2">
                      {l}
                      {v === "bookings" && (stats.pending_bookings ?? 0) > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-medium bg-gold text-ink" data-testid="bookings-pending-badge">
                          {stats.pending_bookings}
                        </span>
                      )}
                      {v === "estimates" && (stats.new_estimates ?? 0) > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-medium bg-gold text-ink" data-testid="estimates-new-badge">
                          {stats.new_estimates}
                        </span>
                      )}
                    </span>
                    {tab === v && <Check size={15} className="text-gold" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop: riga di tab */}
          <div className="hidden md:block overflow-x-auto -mx-2 px-2">
            <TabsList className="bg-white border border-hair rounded-none p-1 flex w-max min-w-full">
              {SECTIONS.map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-none whitespace-nowrap">
                  {l}
                  {v === "bookings" && (stats.pending_bookings ?? 0) > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-medium bg-gold text-ink" data-testid="bookings-pending-badge-desktop">
                      {stats.pending_bookings}
                    </span>
                  )}
                  {v === "estimates" && (stats.new_estimates ?? 0) > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-medium bg-gold text-ink" data-testid="estimates-new-badge-desktop">
                      {stats.new_estimates}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="portfolio" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <button onClick={openNew} className="btn-ink inline-flex items-center justify-center gap-2 w-full md:w-auto min-h-[44px]" data-testid={ADMIN.newProject}>
                    <Plus size={16} /> Nuovo Progetto
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">{editingId ? "Modifica progetto" : "Nuovo progetto"}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={onSave} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Titolo</Label>
                        <Input required value={form.title} onChange={set("title")} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label>Categoria</Label>
                        <Select value={form.category} onValueChange={(v)=>setForm({...form,category:v})}>
                          <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Residenziale">Residenziale</SelectItem>
                            <SelectItem value="Ristrutturazione">Ristrutturazione</SelectItem>
                            <SelectItem value="Home Staging">Home Staging</SelectItem>
                            <SelectItem value="Commerciale">Commerciale</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Luogo</Label>
                        <Input value={form.location} onChange={set("location")} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label>Anno</Label>
                        <Input value={form.year} onChange={set("year")} className="rounded-none mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label>Cover Image</Label>
                      <div className="mt-2">
                        <ImageField
                          value={form.cover_image}
                          onChange={(v) => setForm({ ...form, cover_image: v })}
                          testId="project-cover"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Galleria Immagini</Label>
                      <div className="mt-2">
                        <MultiImageField
                          value={form.images}
                          onChange={(imgs) => setForm({ ...form, images: imgs })}
                          testId="project-gallery"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Descrizione</Label>
                      <Textarea rows={4} value={form.description} onChange={set("description")} className="rounded-none mt-1" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch checked={form.featured} onCheckedChange={(v)=>setForm({...form, featured: v})} />
                      <span className="text-sm">In evidenza (Home)</span>
                    </div>
                    <button type="submit" className="btn-ink w-full" data-testid={ADMIN.saveProject}>Salva</button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {isMobile ? (
              <div className="space-y-3" data-testid="portfolio-cards">
                {items.length === 0 && <div className="p-10 text-center text-taupe bg-white border border-hair">Nessun progetto</div>}
                {items.map((p) => (
                  <div key={p.id} className="bg-white border border-hair p-4" data-testid={`portfolio-card-${p.id}`}>
                    <div className="flex gap-4">
                      <img src={absoluteMediaUrl(p.cover_image)} className="w-20 h-20 object-cover shrink-0" alt="" />
                      <div className="min-w-0 flex-1">
                        <div className="font-serif text-lg leading-snug break-words">{p.title}</div>
                        <div className="text-xs text-taupe mt-1">{p.category}{p.location ? ` · ${p.location}` : ""}</div>
                        {p.featured && <div className="mt-1 inline-flex items-center gap-1 text-xs text-gold"><Star size={12} fill="currentColor" /> In evidenza</div>}
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      <button onClick={() => openEdit(p)} className="min-h-[44px] inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-ink hover:bg-ink hover:text-bone transition-colors" data-testid={ADMIN.editProject(p.id)}><Pencil size={14} /> Modifica</button>
                      <Link to="/portfolio" className="min-h-[44px] inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-hair text-taupe hover:border-ink hover:text-ink transition-colors" data-testid={`admin-view-project-${p.id}`}><Eye size={14} /> Vedi</Link>
                      <button onClick={() => onDelete(p.id)} className="min-h-[44px] inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone transition-colors" data-testid={ADMIN.deleteProject(p.id)}><Trash2 size={14} /> Elimina</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div className="bg-white border border-hair">
              <table className="w-full text-sm">
                <thead className="border-b border-hair text-left">
                  <tr>
                    <th className="p-4 font-normal eyebrow">Cover</th>
                    <th className="p-4 font-normal eyebrow">Titolo</th>
                    <th className="p-4 font-normal eyebrow">Categoria</th>
                    <th className="p-4 font-normal eyebrow">Luogo</th>
                    <th className="p-4 font-normal eyebrow">Featured</th>
                    <th className="p-4 font-normal eyebrow text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p)=>(
                    <tr key={p.id} className="border-b border-hair last:border-0">
                      <td className="p-4"><img src={absoluteMediaUrl(p.cover_image)} className="w-16 h-16 object-cover" alt="" /></td>
                      <td className="p-4 font-serif text-lg">{p.title}</td>
                      <td className="p-4 text-taupe">{p.category}</td>
                      <td className="p-4 text-taupe">{p.location}</td>
                      <td className="p-4">{p.featured && <Star size={16} className="text-gold" fill="currentColor" />}</td>
                      <td className="p-4 text-right">
                        <button onClick={()=>openEdit(p)} className="p-2 hover:text-gold" data-testid={ADMIN.editProject(p.id)}><Pencil size={16} /></button>
                        <button onClick={()=>onDelete(p.id)} className="p-2 hover:text-red-600" data-testid={ADMIN.deleteProject(p.id)}><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            {isMobile ? (
              <div className="space-y-3" data-testid="bookings-cards">
                {bookings.length === 0 && <div className="p-10 text-center text-taupe bg-white border border-hair">Nessuna prenotazione</div>}
                {bookings.map((b) => (
                  <div key={b.id} className="bg-white border border-hair p-4" data-testid={`booking-card-${b.id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0">
                        <div className="font-serif text-lg leading-snug break-words">{b.name}</div>
                        <div className="text-xs text-taupe mt-1 break-all">{b.email}<br />{b.phone}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-medium">{b.date}</div>
                        <div className="text-xs text-taupe">{b.time_slot} · {b.consultation_type}</div>
                        <span className={`inline-block mt-1 text-[10px] uppercase tracking-[0.14em] ${BOOKING_STATUS_COLORS[b.status] || "text-gold"}`} data-testid={`booking-status-${b.id}`}>{BOOKING_STATUS_LABELS[b.status] || b.status}</span>
                      </div>
                    </div>
                    <BookingMeta b={b} />
                    <BookingActions b={b} onAction={bookingAction} onConfirm={askConfirmBooking} onEdit={openEditBooking} onArchive={archiveBooking} onTrash={trashBooking} />
                  </div>
                ))}
              </div>
            ) : (
            <div className="bg-white border border-hair overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="border-b border-hair text-left">
                  <tr>
                    <th className="p-4 font-normal eyebrow">Data</th>
                    <th className="p-4 font-normal eyebrow">Cliente</th>
                    <th className="p-4 font-normal eyebrow">Tipo</th>
                    <th className="p-4 font-normal eyebrow">Stato</th>
                    <th className="p-4 font-normal eyebrow">Email</th>
                    <th className="p-4 font-normal eyebrow">Calendar</th>
                    <th className="p-4 font-normal eyebrow text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-taupe">Nessuna prenotazione</td></tr>}
                  {bookings.map((b)=>(
                    <tr key={b.id} className="border-b border-hair last:border-0 align-top">
                      <td className="p-4"><div>{b.date}</div><div className="text-xs text-taupe">{b.time_slot}</div></td>
                      <td className="p-4"><div className="font-serif text-lg">{b.name}</div><div className="text-taupe text-xs">{b.email}<br/>{b.phone}</div></td>
                      <td className="p-4">{b.consultation_type}</td>
                      <td className="p-4">
                        <span className={`text-xs uppercase tracking-[0.14em] ${BOOKING_STATUS_COLORS[b.status] || "text-gold"}`} data-testid={`booking-status-${b.id}`}>{BOOKING_STATUS_LABELS[b.status] || b.status}</span>
                        {b.last_error && <div className="text-[10px] text-red-700 mt-1 max-w-[180px]" data-testid={`booking-error-${b.id}`}>{b.last_error}</div>}
                      </td>
                      <td className="p-4 text-center"><YesNo v={b.email_final_sent} /></td>
                      <td className="p-4 text-center">
                        <YesNo v={!!b.gcal_event_id} />
                        {b.gcal_event_id && <div className="text-[9px] text-taupe break-all max-w-[110px]" title={b.gcal_event_id}>…{b.gcal_event_id.slice(-8)}</div>}
                      </td>
                      <td className="p-4 text-right">
                        <BookingActions b={b} onAction={bookingAction} onConfirm={askConfirmBooking} onEdit={openEditBooking} onArchive={archiveBooking} onTrash={trashBooking} compact />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            {/* Modale modifica prenotazione */}
            <Dialog open={!!editingBooking} onOpenChange={(o) => !o && setEditingBooking(null)}>
              <DialogContent className="rounded-none" data-testid="booking-edit-dialog">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Modifica prenotazione</DialogTitle></DialogHeader>
                <p className="text-xs text-taupe">Le modifiche a data/orario vengono verificate contro gli altri impegni e sincronizzate con Google Calendar quando collegato.</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Data</Label>
                    <Input type="date" value={bForm.date || ""} onChange={(e) => setBForm({ ...bForm, date: e.target.value })} className="rounded-none mt-1" data-testid="booking-edit-date" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Orario</Label>
                    <select value={bForm.time_slot || ""} onChange={(e) => setBForm({ ...bForm, time_slot: e.target.value })} className="h-10 w-full border border-hair bg-white px-3 text-sm rounded-none mt-1" data-testid="booking-edit-slot">
                      {(settings?.availability_slots || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome e cognome</Label>
                    <Input value={bForm.name || ""} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} className="rounded-none mt-1" data-testid="booking-edit-name" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Telefono</Label>
                    <Input value={bForm.phone || ""} onChange={(e) => setBForm({ ...bForm, phone: e.target.value })} className="rounded-none mt-1" data-testid="booking-edit-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Email</Label>
                    <Input type="email" value={bForm.email || ""} onChange={(e) => setBForm({ ...bForm, email: e.target.value })} className="rounded-none mt-1" data-testid="booking-edit-email" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Note</Label>
                    <Textarea rows={3} value={bForm.notes || ""} onChange={(e) => setBForm({ ...bForm, notes: e.target.value })} className="rounded-none mt-1" data-testid="booking-edit-notes" />
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-3">
                  <button onClick={() => setEditingBooking(null)} className="min-h-[44px] text-xs uppercase tracking-[0.12em] border border-hair px-4 hover:border-ink transition-colors">Annulla</button>
                  <button onClick={saveBookingEdit} className="min-h-[44px] text-xs uppercase tracking-[0.12em] bg-ink text-bone px-5 hover:bg-gold hover:text-ink transition-colors" data-testid="booking-edit-save">Salva modifiche</button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            <RequestsPanel />
          </TabsContent>

          <TabsContent value="banner" className="mt-6">
            <BannersPanel />
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <MessagesPanel contacts={contacts} reload={refresh} />
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <div className="mb-8">
              <SectionImagePanel section="shop_hero" title="Immagine hero della pagina Shop"
                note="Immagine di sfondo in cima alla pagina Shop (mostrata con overlay scuro). Formati: JPG, JPEG, PNG, WEBP · max 10 MB." />
            </div>
            <ShopCategoriesPanel onChanged={refresh} />
            <div className="flex justify-end mb-4 mt-6 gap-2 flex-wrap">
              <button onClick={() => setImportOpen(true)} className="inline-flex items-center justify-center gap-2 w-full md:w-auto min-h-[44px] border border-ink px-5 text-xs tracking-[0.14em] uppercase hover:bg-ink hover:text-bone transition-colors" data-testid="admin-import-product">
                <Plus size={16} /> Importa da link
              </button>
              <Dialog open={importOpen} onOpenChange={setImportOpen}>
                <DialogContent className="max-w-lg rounded-none" data-testid="import-product-dialog">
                  <DialogHeader><DialogTitle className="font-serif text-2xl">Importa prodotto da link</DialogTitle></DialogHeader>
                  <p className="text-xs text-taupe leading-relaxed">Incolla l'URL della pagina prodotto dal sito del produttore o rivenditore. Il sistema compila automaticamente la scheda con i dati disponibili (nome, brand, immagini, dimensioni, materiali…). <strong>Prezzo e disponibilità non vengono mai importati</strong>: li imposti tu prima di salvare. Il prodotto resta non pubblicato finché non lo revisioni.</p>
                  <div className="mt-4 space-y-3">
                    <div>
                      <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Incolla URL prodotto</Label>
                      <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://…" className="rounded-none mt-1" data-testid="import-url-input" />
                    </div>
                    <button onClick={analyzeImport} disabled={importBusy || !importUrl.trim()} className="btn-ink w-full min-h-[48px] disabled:opacity-40" data-testid="import-analyze-btn">
                      {importBusy ? "Analisi in corso…" : "Analizza prodotto"}
                    </button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                <DialogTrigger asChild>
                  <button onClick={openNewProduct} className="btn-ink inline-flex items-center justify-center gap-2 w-full md:w-auto min-h-[44px]" data-testid="admin-new-product">
                    <Plus size={16} /> Nuovo Prodotto
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">{editingProductId ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={onSaveProduct} className="space-y-4">
                    {productForm.source_url && (
                      <p className="text-[11px] text-taupe border border-hair bg-bone p-3 break-all" data-testid="product-import-source">Bozza importata da: {productForm.source_url} — prezzo e disponibilità vanno impostati manualmente.</p>
                    )}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Nome</Label>
                        <Input required value={productForm.name} onChange={setP("name")} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label>Categoria</Label>
                        <Select value={productForm.category} onValueChange={(v)=>setProductForm({...productForm,category:v})}>
                          <SelectTrigger className="rounded-none mt-1" data-testid="product-category-select"><SelectValue placeholder="Scegli categoria" /></SelectTrigger>
                          <SelectContent>
                            {shopCategories.map((c)=>(
                              <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Prezzo (EUR)</Label>
                        <Input required type="number" step="0.01" value={productForm.price} onChange={setP("price")} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label>Disponibilità</Label>
                        <Select value={productForm.availability || "disponibile"} onValueChange={(v)=>setProductForm({...productForm, availability: v})}>
                          <SelectTrigger className="rounded-none mt-1" data-testid="product-availability-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="disponibile">Disponibile</SelectItem>
                            <SelectItem value="limitata">Disponibilità limitata</SelectItem>
                            <SelectItem value="da_ordinare">Da ordinare</SelectItem>
                            <SelectItem value="temporaneamente_non_disponibile">Temporaneamente non disponibile</SelectItem>
                            <SelectItem value="esaurito">Esaurito</SelectItem>
                            <SelectItem value="su_richiesta">Su richiesta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(productForm.availability === "da_ordinare") && (
                        <>
                        <div>
                          <Label>Tempistica (obbligatoria)</Label>
                          <Select value={productForm.lead_time} onValueChange={(v)=>setProductForm({...productForm, lead_time: v})}>
                            <SelectTrigger className="rounded-none mt-1" data-testid="product-leadtime-select"><SelectValue placeholder="Scegli tempistica" /></SelectTrigger>
                            <SelectContent>
                              {["2–3 settimane", "30 giorni", "6–8 settimane"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Testo tempistica personalizzato (opz.)</Label>
                          <Input value={productForm.lead_days || ""} onChange={setP("lead_days")} placeholder="es. 4 settimane dal saldo" className="rounded-none mt-1" data-testid="product-lead-custom" />
                        </div>
                        <div>
                          <Label>Data indicativa consegna (opz.)</Label>
                          <Input type="date" value={productForm.lead_date || ""} onChange={setP("lead_date")} className="rounded-none mt-1" data-testid="product-lead-date" />
                        </div>
                        </>
                      )}
                      <div>
                        <Label>Codice prodotto / SKU</Label>
                        <Input value={productForm.sku || ""} onChange={setP("sku")} className="rounded-none mt-1" data-testid="product-sku" />
                      </div>
                      <div>
                        <Label>Prezzo precedente (barrato, opz.)</Label>
                        <Input type="number" step="0.01" min={0} value={productForm.compare_price || 0} onChange={setP("compare_price")} className="rounded-none mt-1" data-testid="product-compare-price" />
                      </div>
                      <div>
                        <Label>Collezione</Label>
                        <Input value={productForm.collection || ""} onChange={setP("collection")} className="rounded-none mt-1" data-testid="product-collection" />
                      </div>
                      <div>
                        <Label>Materiali</Label>
                        <Input value={productForm.materials || ""} onChange={setP("materials")} className="rounded-none mt-1" data-testid="product-materials" />
                      </div>
                      <div>
                        <Label>Colori</Label>
                        <Input value={productForm.colors || ""} onChange={setP("colors")} className="rounded-none mt-1" data-testid="product-colors" />
                      </div>
                      <div>
                        <Label>Dimensioni</Label>
                        <Input value={productForm.dimensions || ""} onChange={setP("dimensions")} placeholder="es. 160×90×75 cm" className="rounded-none mt-1" data-testid="product-dimensions" />
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Switch checked={productForm.published !== false} onCheckedChange={(v)=>setProductForm({...productForm, published: v})} data-testid="product-published-switch" />
                        <span className="text-sm">Pubblicato sul sito</span>
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Switch checked={!!productForm.is_new} onCheckedChange={(v)=>setProductForm({...productForm, is_new: v})} data-testid="product-is-new" />
                        <span className="text-sm">Etichetta "Novità"</span>
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Switch checked={!!productForm.price_on_request} onCheckedChange={(v)=>setProductForm({...productForm, price_on_request: v})} data-testid="product-por" />
                        <span className="text-sm">Prezzo esclusivo su richiesta (non acquistabile online)</span>
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Switch checked={productForm.purchasable !== false} onCheckedChange={(v)=>setProductForm({...productForm, purchasable: v})} data-testid="product-purchasable" />
                        <span className="text-sm">Acquistabile online</span>
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Switch checked={productForm.info_request !== false} onCheckedChange={(v)=>setProductForm({...productForm, info_request: v})} data-testid="product-info-request" />
                        <span className="text-sm">Consenti richiesta informazioni</span>
                      </div>
                      <div>
                        <Label>Brand</Label>
                        <Input value={productForm.brand || ""} onChange={setP("brand")} className="rounded-none mt-1" data-testid="product-brand" />
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Switch checked={!!productForm.studio_discount_excluded} onCheckedChange={(v)=>setProductForm({...productForm, studio_discount_excluded: v})} data-testid="product-studio-excluded" />
                        <span className="text-sm">Escluso dal vantaggio Clienti Studio</span>
                      </div>
                    </div>
                    {/* Varianti */}
                    <div className="border border-hair p-4" data-testid="product-variants-editor">
                      <Label>Varianti (opzionali)</Label>
                      <div className="space-y-2 mt-2">
                        {(productForm.variants || []).map((v, i) => (
                          <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end border-b border-hair pb-2" data-testid={`variant-row-${i}`}>
                            <Input placeholder="Nome variante" value={v.name || ""} onChange={(e)=>{const vs=[...productForm.variants];vs[i]={...vs[i],name:e.target.value};setProductForm({...productForm,variants:vs});}} className="rounded-none" data-testid={`variant-name-${i}`} />
                            <Input placeholder="SKU" value={v.sku || ""} onChange={(e)=>{const vs=[...productForm.variants];vs[i]={...vs[i],sku:e.target.value};setProductForm({...productForm,variants:vs});}} className="rounded-none" data-testid={`variant-sku-${i}`} />
                            <Input type="number" step="0.01" placeholder="Prezzo (vuoto = base)" value={v.price ?? ""} onChange={(e)=>{const vs=[...productForm.variants];vs[i]={...vs[i],price:e.target.value===""?null:Number(e.target.value)};setProductForm({...productForm,variants:vs});}} className="rounded-none" data-testid={`variant-price-${i}`} />
                            <Input placeholder="Tempistica" value={v.lead_time || ""} onChange={(e)=>{const vs=[...productForm.variants];vs[i]={...vs[i],lead_time:e.target.value};setProductForm({...productForm,variants:vs});}} className="rounded-none" data-testid={`variant-lead-${i}`} />
                            <button type="button" onClick={()=>setProductForm({...productForm,variants:productForm.variants.filter((_,j)=>j!==i)})} className="min-h-[44px] text-xs text-red-700 border border-red-700 hover:bg-red-700 hover:text-bone transition-colors" data-testid={`variant-del-${i}`}>Elimina</button>
                          </div>
                        ))}
                        <button type="button" onClick={()=>setProductForm({...productForm,variants:[...(productForm.variants||[]),{name:"",sku:"",price:null,availability:"disponibile",lead_time:""}]})} className="min-h-[44px] px-4 text-xs tracking-[0.14em] uppercase border border-ink hover:bg-ink hover:text-bone transition-colors" data-testid="variant-add">+ Aggiungi variante</button>
                      </div>
                    </div>
                    <div>
                      <Label>Immagine Cover</Label>
                      <div className="mt-2">
                        <ImageField
                          value={productForm.image}
                          onChange={(v) => setProductForm({ ...productForm, image: v })}
                          testId="product-image"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Descrizione breve</Label>
                      <Input value={productForm.short_description} onChange={setP("short_description")} className="rounded-none mt-1" />
                    </div>
                    <div>
                      <Label>Galleria immagini</Label>
                      <div className="mt-2">
                        <GalleryManager
                          items={productForm.gallery || []}
                          onChange={(g) => setProductForm({ ...productForm, gallery: g })}
                          coverUrl={productForm.image}
                          onCover={(url) => setProductForm({ ...productForm, image: url })}
                          testId="product-gallery"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Descrizione</Label>
                      <Textarea rows={4} value={productForm.description} onChange={setP("description")} className="rounded-none mt-1" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch checked={productForm.featured} onCheckedChange={(v)=>setProductForm({...productForm, featured: v})} />
                      <span className="text-sm">In evidenza</span>
                    </div>
                    <button type="submit" className="btn-ink w-full" data-testid="admin-save-product">Salva</button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {isMobile ? (
              <div className="space-y-3" data-testid="products-cards">
                {products.length === 0 && <div className="p-10 text-center text-taupe bg-white border border-hair">Nessun prodotto</div>}
                {products.map((p) => (
                  <div key={p.id} className="bg-white border border-hair p-4" data-testid={`product-card-${p.id}`}>
                    <div className="flex gap-4">
                      <img src={absoluteMediaUrl(p.image)} className="w-20 h-20 object-cover shrink-0" alt="" />
                      <div className="min-w-0 flex-1">
                        <div className="font-serif text-lg leading-snug break-words">{p.name} {p.featured && <Star size={12} className="inline text-gold" fill="currentColor" />}</div>
                        <div className="text-xs text-taupe mt-1">{p.category}</div>
                        <div className="mt-1 flex items-center gap-3">
                          <span className="font-serif">{eur(p.price)}</span>
                          <span className={`text-[10px] uppercase tracking-[0.14em] ${AVAIL_COLORS[p.availability || "disponibile"]}`} data-testid={`product-avail-${p.id}`}>{AVAIL_LABELS[p.availability || "disponibile"]}{p.availability === "da_ordinare" && p.lead_time ? ` · ${p.lead_time}` : ""}</span>
                          {p.published === false && <span className="text-[10px] uppercase tracking-[0.14em] text-amber-700 border border-amber-700/40 px-1.5 py-0.5" data-testid={`product-draft-${p.id}`}>Bozza</span>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button onClick={() => openEditProduct(p)} className="min-h-[44px] inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-ink hover:bg-ink hover:text-bone transition-colors" data-testid={`admin-edit-product-${p.id}`}><Pencil size={14} /> Modifica</button>
                      <button onClick={() => onDeleteProduct(p.id)} className="min-h-[44px] inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone transition-colors" data-testid={`admin-delete-product-${p.id}`}><Trash2 size={14} /> Elimina</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div className="bg-white border border-hair">
              <table className="w-full text-sm">
                <thead className="border-b border-hair text-left">
                  <tr>
                    <th className="p-4 font-normal eyebrow">Immagine</th>
                    <th className="p-4 font-normal eyebrow">Nome</th>
                    <th className="p-4 font-normal eyebrow">Categoria</th>
                    <th className="p-4 font-normal eyebrow">Prezzo</th>
                    <th className="p-4 font-normal eyebrow">Stock</th>
                    <th className="p-4 font-normal eyebrow text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-taupe">Nessun prodotto</td></tr>}
                  {products.map((p)=>(
                    <tr key={p.id} className="border-b border-hair last:border-0">
                      <td className="p-4"><img src={absoluteMediaUrl(p.image)} className="w-16 h-16 object-cover" alt="" /></td>
                      <td className="p-4 font-serif text-lg">{p.name} {p.featured && <Star size={12} className="inline text-gold" fill="currentColor" />} {p.published === false && <span className="text-[10px] uppercase tracking-[0.14em] text-amber-700 border border-amber-700/40 px-1.5 py-0.5 align-middle">Bozza</span>}</td>
                      <td className="p-4 text-taupe">{p.category}</td>
                      <td className="p-4">{eur(p.price)}</td>
                      <td className="p-4">
                        <span className={`text-xs uppercase tracking-[0.14em] ${AVAIL_COLORS[p.availability || "disponibile"]}`}>{AVAIL_LABELS[p.availability || "disponibile"]}</span>
                      </td>
                      <td className="p-4 text-right">
                        <button onClick={()=>openEditProduct(p)} className="p-2 hover:text-gold" data-testid={`admin-edit-product-${p.id}`}><Pencil size={16} /></button>
                        <button onClick={()=>onDeleteProduct(p.id)} className="p-2 hover:text-red-600" data-testid={`admin-delete-product-${p.id}`}><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">

            <QuotesPanel />
            <div className="mt-12">
              <div className="eyebrow mb-4">Preventivi Shop</div>
              <ShopQuotesPanel items={inquiries} reload={refresh} />
            </div>
          </TabsContent>

          <TabsContent value="estimates" className="mt-6">
            <EstimatesPanel />
          </TabsContent>

          <TabsContent value="estimator" className="mt-6">
            <EstimatorConfigPanel />
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <OrdersPanel orders={orders} reload={refresh} />
          </TabsContent>

          <TabsContent value="crm" className="mt-6">
            <CrmPanel />
          </TabsContent>

          <TabsContent value="library" className="mt-6">
            <LibraryPanel />
          </TabsContent>

          <TabsContent value="shoptheproject" className="mt-6">
            <div className="space-y-6 mb-8">
              <SectionImagePanel section="home_stp" title='Immagine homepage — sezione "Shop the Project"' />
              <SectionImagePanel section="stp_page" title='Immagine della pagina "Shop the Project"' />
            </div>
            <ShoppingListsPanel />
          </TabsContent>

          <TabsContent value="automazioni" className="mt-6">
            <AutomationsPanel />
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            <BlogPanel />
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <AnalyticsPanel />
          </TabsContent>

          <TabsContent value="newsletter" className="mt-6">
            <NewsletterContacts />
          </TabsContent>

          <TabsContent value="samples" className="mt-6">
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {servicesForSamples.map((svc) => (
                <ServiceSampleCard key={svc.slug} svc={svc} sample={samples[svc.slug]} onSaved={refresh} />
              ))}
            </div>
            <p className="text-xs text-taupe mt-6">Carica un PDF di esempio per ogni servizio. Il PDF non è mai pubblico: il visitatore compila il modulo "Ricevi un estratto del progetto" e lo riceve via email. Puoi attivare/disattivare la raccolta lead e vedere il numero di richieste. Max 20 MB.</p>
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <div className="space-y-3" data-testid="pdf-leads-list">
              {pdfLeads.length === 0 && <div className="p-10 text-center text-taupe bg-white border border-hair">Nessun lead PDF ancora.</div>}
              {pdfLeads.map((l) => (
                <div key={l.id} className="bg-white border border-hair p-4 md:p-5" data-testid={`pdf-lead-${l.id}`}>
                  <div className="flex justify-between items-start gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-serif text-lg leading-snug">{l.name}</div>
                      <div className="text-xs text-taupe mt-1 break-all">{l.email}{l.phone ? ` · ${l.phone}` : ""}</div>
                      <div className="text-xs text-taupe mt-1">
                        {[l.property_type, l.city, l.area_sqm && `${l.area_sqm} mq`, l.project_phase].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs uppercase tracking-[0.12em] text-gold">{l.service_title || l.service_slug}</div>
                      <div className="text-[11px] text-taupe mt-1">{new Date(l.created_at).toLocaleString("it-IT")}</div>
                      <div className="text-[11px] mt-1">
                        <span className={l.email_sent ? "text-emerald-700" : "text-red-700"}>{l.email_sent ? "PDF inviato" : "Email non inviata"}</span>
                        {" · "}
                        <span className="text-taupe">Newsletter: {l.newsletter_consent ? "sì" : "no"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="services" className="mt-6">
            <ServicesPanel />
          </TabsContent>

          <TabsContent value="pagine" className="mt-6">
            <PagesPanel />
          </TabsContent>

          <TabsContent value="giftcards" className="mt-6">
            <GiftCardsPanel settings={settings} onSaveSettings={saveSettings} />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            {settings && (
              <SettingsPanel initial={settings} onSave={saveSettings} busy={savingSettings} />
            )}
          </TabsContent>

          <TabsContent value="backup" className="mt-6">
            <div className="bg-white border border-hair p-5 md:p-8 max-w-2xl">
              <div className="eyebrow">Backup & Migrazione</div>
              <h3 className="font-serif text-3xl mt-3">Esporta tutto, ripartì ovunque.</h3>
              <p className="text-taupe mt-4 text-sm leading-relaxed">
                Il <strong>backup completo ZIP</strong> contiene: tutti i dati (backup.json), tutte le immagini originali di portfolio, blog, prodotti e servizi, i PDF dei preventivi e dei Materiali, il manifest con checksum SHA-256 e collegamento record↔file, la documentazione, il file .env di esempio, lo schema del database e lo script di ripristino (restore.py). Per reinstallare altrove serve anche il <strong>codice sorgente</strong> (pulsante dedicato qui sotto): insieme permettono la reinstallazione completa senza Emergent.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <button onClick={() => downloadFile("/admin/export.zip", "backup-completo.zip")} className="btn-ink min-h-[44px] inline-flex items-center justify-center gap-2 w-full" data-testid="admin-export-zip">
                  Scarica backup completo ZIP
                </button>
                <button onClick={() => downloadFile("/admin/export/code.zip", "codice-sorgente.zip")} className="min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.14em] uppercase border border-ink px-4 py-2 hover:bg-ink hover:text-bone transition-colors w-full" data-testid="admin-export-code">
                  Scarica codice sorgente (ZIP)
                </button>
                <button onClick={doExport} className="min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.14em] uppercase border border-ink px-4 py-2 hover:bg-ink hover:text-bone transition-colors w-full" data-testid="admin-export-btn">
                  Scarica solo dati JSON
                </button>
                <button onClick={() => downloadFile("/admin/export/docs", "MIGRATION.md")} className="min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.14em] uppercase border border-ink px-4 py-2 hover:bg-ink hover:text-bone transition-colors w-full" data-testid="admin-export-docs">
                  Scarica documentazione di migrazione
                </button>
              </div>
              <div className="mt-8 pt-6 border-t border-hair">
                <div className="eyebrow text-[10px] mb-3">Export parziali (CSV)</div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={exportBookingsCsv} className="min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.14em] uppercase border border-hair text-taupe px-4 py-2 hover:border-ink hover:text-ink transition-colors w-full sm:w-auto" data-testid="admin-export-bookings-csv">
                    Prenotazioni (CSV)
                  </button>
                  <button onClick={exportContactsCsv} className="min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.14em] uppercase border border-hair text-taupe px-4 py-2 hover:border-ink hover:text-ink transition-colors w-full sm:w-auto" data-testid="admin-export-contacts-csv">
                    Messaggi (CSV)
                  </button>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-hair text-xs text-taupe leading-relaxed">
                <strong>Ripristino:</strong> su un nuovo ambiente basta eseguire <code className="bg-hair px-1">python3 restore.py --zip backup.zip --mongo-url … --db-name … --storage-dir … --media-base-url …</code>. Lo script importa i dati, carica i file sul nuovo storage, riscrive automaticamente gli URL e produce un report dei file mancanti. Password, token e chiavi API non vengono mai esportati. Documentazione completa: <code className="bg-hair px-1">docs/IMPORT.md</code> nello ZIP.
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
